/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import Select from 'react-select';
import { MapPin, Navigation, Search, Check, X, RotateCcw, Loader2, ArrowUpDown, Store } from 'lucide-react';
import { useMunicipalities } from './MunicipalitiesContext';
import { useIsMobile } from '../utils/useIsMobile';
import { productsService, municipalitiesService } from '../api';
import ProductCard from './ProductCard';
import ProductDetailsModal from './ProductDetailsModal';
import NearestLocationModal from './NearestLocationModal';
import { useToast } from './Toast';
import { useCategories } from './CategoriesContext';
import { trackEvent, trackProductView } from '../utils/analytics';

const PAGE_SIZE = 20;
const SEARCH_QUERY_DEBOUNCE_MS = 500;
const PRICE_DEBOUNCE_MS = 350;

const SORT_OPTIONS = [
  { value: 'newest', label: 'Najnovije' },
  { value: 'oldest', label: 'Najstarije' },
  { value: 'priceAsc', label: 'Cena: rastuće' },
  { value: 'priceDesc', label: 'Cena: opadajuće' },
  { value: 'nearest', label: 'Najbliži' },
];

const sortSelectStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

const sortSelectClassNames = {
  control: ({ isFocused }) =>
    `bg-transparent border-none cursor-pointer transition-all min-h-0 ${isFocused ? '' : ''}`,
  valueContainer: () => 'p-0',
  placeholder: () => 'text-sage-400 text-xs font-bold',
  singleValue: () => 'text-earth-900 text-xs font-bold',
  menu: () => 'bg-white rounded-2xl shadow-2xl border border-sage-100 overflow-hidden mt-2 z-20 min-w-[170px]',
  menuList: () => 'py-2 max-h-60',
  option: ({ isFocused, isSelected }) =>
    `cursor-pointer px-4 py-2 text-xs font-bold transition-colors ${
      isSelected
        ? 'bg-sage-600 text-white'
        : isFocused
        ? 'bg-sage-50 text-earth-900'
        : 'bg-white text-earth-900'
    }`,
  indicatorSeparator: () => 'hidden',
  dropdownIndicator: () => 'text-sage-400 hover:text-sage-600 px-1 transition-colors',
};

function ProductCardSkeleton() {
  return (
    <div className="organic-card animate-pulse">
      <div className="relative aspect-[4/3] bg-sage-50" />
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="h-6 w-1/2 bg-sage-50 rounded-lg" />
          <div className="h-6 w-1/4 bg-sage-50 rounded-lg" />
        </div>
        <div className="space-y-2 mb-6">
          <div className="h-4 w-full bg-sage-50 rounded-lg" />
          <div className="h-4 w-2/3 bg-sage-50 rounded-lg" />
        </div>
        <div className="space-y-3 border-t border-sage-50 pt-4">
          <div className="h-3 w-1/3 bg-sage-50 rounded-lg" />
          <div className="h-3 w-1/2 bg-sage-50 rounded-lg" />
        </div>
        <div className="h-12 w-full bg-sage-50 rounded-xl mt-6" />
      </div>
    </div>
  );
}

export default function ProductGrid({ globalSearchQuery = '', onResetSearch }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeCategory = searchParams.get('kategorija') || 'Svi';
  const locationSearch = searchParams.get('opstina') || '';
  const priceMin = searchParams.get('cenaMin') || '';
  const priceMax = searchParams.get('cenaMax') || '';
  const sortBy = searchParams.get('sort') || 'newest';
  const nearLat = searchParams.get('nearLat') || '';
  const nearLng = searchParams.get('nearLng') || '';
  const nearCity = searchParams.get('nearCity') || '';
  const oglasParam = searchParams.get('oglas') || '';
  const prodavacId = searchParams.get('prodavac') || '';
  const prodavacIme = searchParams.get('prodavacIme') || '';

  const isMobile = useIsMobile();
  const { names: gradovi } = useMunicipalities();
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [locationInput, setLocationInput] = useState(locationSearch);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [isNearestModalOpen, setIsNearestModalOpen] = useState(false);
  const [isResolvingNearest, setIsResolvingNearest] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [debouncedQuery, setDebouncedQuery] = useState(globalSearchQuery);
  const [debouncedPriceMin, setDebouncedPriceMin] = useState(priceMin);
  const [debouncedPriceMax, setDebouncedPriceMax] = useState(priceMax);

  const dropdownRef = useRef(null);
  const sentinelRef = useRef(null);
  const requestSeqRef = useRef(0);
  const toast = useToast();
  const { names: categoryNames } = useCategories();
  const selectedProductRef = useRef(selectedProduct);
  selectedProductRef.current = selectedProduct;

  const updateParams = useCallback((updates, { replace = true } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === '' || v == null) next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace });
  }, [setSearchParams]);

  const setActiveCategory = useCallback((cat) => {
    updateParams({ kategorija: cat === 'Svi' ? '' : cat });
    trackEvent('filter_apply', { filter_type: 'category', filter_value: cat });
  }, [updateParams]);
  const setLocationSearch = useCallback((loc) => {
    updateParams({ opstina: loc });
  }, [updateParams]);
  const setPriceMin = useCallback((min) => {
    updateParams({ cenaMin: min });
  }, [updateParams]);
  const setPriceMax = useCallback((max) => {
    updateParams({ cenaMax: max });
  }, [updateParams]);
  const setSortBy = useCallback((value) => {
    updateParams({
      sort: value === 'newest' ? '' : value,
      nearLat: '',
      nearLng: '',
      nearCity: '',
    });
  }, [updateParams]);

  const applyNearestFromCity = useCallback((city) => {
    updateParams({ sort: 'nearest', nearCity: city, nearLat: '', nearLng: '' });
  }, [updateParams]);

  const applyNearestFromCoords = useCallback((lat, lng) => {
    updateParams({ sort: 'nearest', nearLat: String(lat), nearLng: String(lng), nearCity: '' });
  }, [updateParams]);

  const openProduct = useCallback((product) => {
    setSelectedProduct(product);
    updateParams({ oglas: product?.slug || product?.id || '' }, { replace: false });
    trackProductView(product);
  }, [updateParams]);

  const closeProduct = useCallback(() => {
    setSelectedProduct(null);
    updateParams({ oglas: '' }, { replace: false });
  }, [updateParams]);

  // Sync selected product with ?oglas= param (handles paste, back/forward, deep links).
  useEffect(() => {
    if (!oglasParam) {
      setSelectedProduct(null);
      return;
    }
    const current = selectedProductRef.current;
    if (current && (current.slug === oglasParam || current.id === oglasParam || current._id === oglasParam)) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await productsService.getOne(oglasParam);
        if (!cancelled) {
          setSelectedProduct(res.data);
          trackProductView(res.data);
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const msg = status === 404
          ? 'Oglas iz linka više ne postoji.'
          : status === undefined
            ? 'Backend ne odgovara — proveri da li je server pokrenut.'
            : (err?.response?.data?.message || 'Greška pri učitavanju oglasa iz linka.');
        console.error('[deep-link] getOne failed', { oglasParam, status, err });
        toast.error(msg);
        setSelectedProduct(null);
      }
    })();
    return () => { cancelled = true; };
  }, [oglasParam, toast]);

  // Debounce search query and price filter (network-bound inputs)
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(globalSearchQuery);
      if (globalSearchQuery && globalSearchQuery.trim()) {
        trackEvent('search', { search_term: globalSearchQuery.trim() });
      }
    }, SEARCH_QUERY_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [globalSearchQuery]);
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedPriceMin(priceMin);
      setDebouncedPriceMax(priceMax);
    }, PRICE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [priceMin, priceMax]);

  const buildParams = useCallback((nextPage) => {
    const params = { page: nextPage, limit: PAGE_SIZE };
    if (activeCategory && activeCategory !== 'Svi') params.category = activeCategory;
    if (locationSearch) params.municipality = locationSearch;
    if (debouncedQuery) params.q = debouncedQuery;
    if (debouncedPriceMin !== '') params.minPrice = debouncedPriceMin;
    if (debouncedPriceMax !== '') params.maxPrice = debouncedPriceMax;
    if (sortBy && sortBy !== 'newest') params.sort = sortBy;
    if (prodavacId) params.domacinstvo = prodavacId;
    if (sortBy === 'nearest') {
      if (nearLat && nearLng) {
        params.nearLat = nearLat;
        params.nearLng = nearLng;
      } else if (nearCity) {
        params.nearCity = nearCity;
      }
    }
    return params;
  }, [activeCategory, locationSearch, debouncedQuery, debouncedPriceMin, debouncedPriceMax, sortBy, nearLat, nearLng, nearCity, prodavacId]);

  const clearSellerFilter = useCallback(() => {
    updateParams({ prodavac: '', prodavacIme: '' });
  }, [updateParams]);

  // Na mount: ako URL ima bilo kakve query parametre (deep link sa filterom/oglasom),
  // skroluj direktno do liste oglasa da korisnik ne mora ručno.
  useEffect(() => {
    if (!window.location.search) return;
    const id = window.requestAnimationFrame(() => {
      const el = document.getElementById('ponuda');
      if (!el) return;
      const headerOffset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSortChange = useCallback((value) => {
    if (value === 'nearest') {
      if (isMobile && 'geolocation' in navigator) {
        setIsResolvingNearest(true);
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setIsResolvingNearest(false);
            applyNearestFromCoords(pos.coords.latitude, pos.coords.longitude);
          },
          (geoErr) => {
            console.error('Geolocation error:', geoErr);
            setIsResolvingNearest(false);
            toast.error('Nismo uspeli da pristupimo vašoj lokaciji. Izaberite opštinu ručno.');
            setIsNearestModalOpen(true);
          }
        );
      } else {
        setIsNearestModalOpen(true);
      }
      return;
    }
    setSortBy(value);
  }, [isMobile, applyNearestFromCoords, setSortBy, toast]);

  const fetchPage = useCallback(async (nextPage, replace) => {
    const seq = ++requestSeqRef.current;
    if (replace) setIsLoading(true);
    else setIsLoadingMore(true);
    setError(null);
    try {
      const res = await productsService.getAll(buildParams(nextPage));
      if (seq !== requestSeqRef.current) return;
      const data = res.data || {};
      setProducts((prev) => (replace ? data.items : [...prev, ...data.items]));
      setHasMore(!!data.hasMore);
      setTotal(data.total || 0);
      setPage(nextPage);
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      setError(err?.response?.data?.message || err?.message || 'Greška prilikom učitavanja oglasa.');
      if (replace) setProducts([]);
    } finally {
      if (seq === requestSeqRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [buildParams]);

  // Reset and fetch first page when filters change
  useEffect(() => {
    fetchPage(1, true);
  }, [fetchPage]);

  // Osveži listu kad korisnik kreira/izmeni oglas (AdModal emituje globalni event),
  // da se novi oglas odmah prikaže bez ručnog osvežavanja stranice.
  useEffect(() => {
    const onProductSaved = () => fetchPage(1, true);
    window.addEventListener('product-saved', onProductSaved);
    return () => window.removeEventListener('product-saved', onProductSaved);
  }, [fetchPage]);

  // Infinite scroll: load next page when sentinel enters viewport
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (!hasMore || isLoading || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchPage(page + 1, false);
        }
      },
      { rootMargin: '300px 0px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore, page, fetchPage]);

  const filteredLocations = useMemo(() => {
    if (!locationInput) return gradovi;
    return gradovi.filter((loc) => loc.toLowerCase().startsWith(locationInput.toLowerCase()));
  }, [locationInput, gradovi]);

  // Kad se dropdown zatvori bez izbora — vrati prikazani tekst na trenutno aktivan filter.
  // Takođe sinhronizuje kad se filter promeni spolja (npr. "Moja lokacija", X dugme, deep link).
  useEffect(() => {
    if (!isLocationDropdownOpen) {
      setLocationInput(locationSearch);
    }
  }, [isLocationDropdownOpen, locationSearch]);

  const handleMyLocation = () => {
    if (!('geolocation' in navigator)) {
      toast.error('Vaš pretraživač ne podržava geolokaciju.');
      return;
    }
    setIsDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await municipalitiesService.findNearest({ lat: latitude, lon: longitude });
          const name = res?.data?.name;
          if (!name) {
            toast.error('Nismo prepoznali opštinu na osnovu vaše lokacije. Izaberite je ručno.');
            return;
          }
          const norm = (s) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();
          const target = norm(name);
          const canonical = gradovi.find((g) => norm(g) === target) || name;
          setLocationSearch(canonical);
          setIsLocationDropdownOpen(false);
          toast.success(`Lokacija postavljena: ${canonical}`);
        } catch (err) {
          console.error('Nearest municipality error:', err);
          const msg = err?.response?.data?.message || 'Greška pri određivanju opštine. Pokušajte ponovo.';
          toast.error(msg);
        } finally {
          setIsDetectingLocation(false);
        }
      },
      (geoErr) => {
        console.error('Error getting location:', geoErr);
        setIsDetectingLocation(false);
        toast.error('Nismo uspeli da pristupimo vašoj lokaciji. Molimo omogućite pristup u podešavanjima pretraživača.');
      }
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLocationDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const categoriesWithAll = ['Svi', ...categoryNames];

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" id="ponuda">
      <div className="mb-12 text-center">
        <h2 className="text-4xl font-serif font-bold text-earth-900 mb-4 tracking-tight">
          Izdvojena ponuda
        </h2>
        <p className="text-sage-600 max-w-2xl leading-relaxed mx-auto">
          Najnoviji sezonski proizvodi direktno sa polja i farmi. Sveže, zdravo i domaće.
        </p>
      </div>

      {prodavacId && (
        <div className="mb-8 flex justify-center">
          <div className="inline-flex items-center gap-3 bg-sage-50 border border-sage-200 rounded-full pl-4 pr-2 py-2 shadow-sm">
            <Store size={16} className="text-sage-600" />
            <span className="text-sm text-sage-700">
              Oglasi prodavca:{' '}
              <span className="font-bold text-earth-900">{prodavacIme || 'odabranog domaćinstva'}</span>
            </span>
            <button
              onClick={clearSellerFilter}
              aria-label="Ukloni filter prodavca"
              className="p-1 rounded-full text-sage-500 hover:text-sage-800 hover:bg-white transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <div className="mb-12 space-y-6">
        <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-center">
          <div className="relative w-full lg:w-80" ref={dropdownRef}>
            <div className="relative group">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-600 group-focus-within:scale-110 transition-transform" size={18} />
              <input
                type="text"
                value={locationInput}
                onFocus={() => setIsLocationDropdownOpen(true)}
                onChange={(e) => {
                  setLocationInput(e.target.value);
                  setIsLocationDropdownOpen(true);
                }}
                placeholder="Pretraži po lokaciji..."
                className="w-full bg-white border border-sage-100 rounded-2xl py-3.5 pl-12 pr-12 focus:ring-4 focus:ring-sage-100/50 focus:border-sage-300 text-sm placeholder-sage-400 transition-all shadow-sm outline-none"
              />
              {(locationInput || locationSearch) && (
                <button
                  onClick={() => {
                    setLocationInput('');
                    setLocationSearch('');
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-sage-300 hover:text-sage-600 transition-colors"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <AnimatePresence>
              {isLocationDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  className="absolute top-full left-0 right-0 mt-3 bg-white rounded-3xl shadow-2xl border border-sage-50 z-50 py-3 overflow-hidden origin-top"
                >
                  {isMobile && (
                    <button
                      onClick={handleMyLocation}
                      disabled={isDetectingLocation}
                      className="w-full px-5 py-4 flex items-center gap-3 text-sage-700 hover:bg-sage-50 transition-colors text-sm font-bold border-b border-sage-50 group"
                    >
                      <div className="w-8 h-8 rounded-xl bg-sage-50 flex items-center justify-center text-sage-600 group-hover:bg-white transition-colors">
                        <Navigation size={16} className={isDetectingLocation ? 'animate-pulse' : ''} />
                      </div>
                      {isDetectingLocation ? 'Detektovanje...' : 'Moja trenutna lokacija'}
                    </button>
                  )}

                  <div className="max-h-60 overflow-y-auto custom-scrollbar">
                    {filteredLocations.length > 0 ? (
                      filteredLocations.map((loc) => (
                        <button
                          key={loc}
                          onClick={() => {
                            setLocationInput(loc);
                            setLocationSearch(loc);
                            setIsLocationDropdownOpen(false);
                          }}
                          className="w-full px-5 py-3.5 flex items-center justify-between text-left hover:bg-sage-50 transition-colors text-sm"
                        >
                          <span className="text-earth-900 font-medium">{loc}</span>
                          {locationSearch === loc && (
                            <div className="w-5 h-5 rounded-full bg-sage-600 flex items-center justify-center text-white">
                              <Check size={12} />
                            </div>
                          )}
                        </button>
                      ))
                    ) : (
                      <div className="px-5 py-8 text-center">
                        <p className="text-sm text-sage-400 italic">Nema rezultata za "{locationInput}"</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex items-center gap-3 bg-white border border-sage-100 rounded-2xl px-4 py-2 shadow-sm justify-center">
            <span className="text-xs font-bold text-sage-500 whitespace-nowrap">Cena (RSD):</span>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="Min"
                value={priceMin}
                onChange={(e) => setPriceMin(e.target.value)}
                className="w-16 bg-sage-50/50 border-none rounded-lg px-2 py-1 text-xs font-bold text-earth-900 focus:ring-2 focus:ring-sage-200 outline-none placeholder-sage-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0"
              />
              <span className="text-sage-300 text-xs">-</span>
              <input
                type="number"
                placeholder="Max"
                value={priceMax}
                onChange={(e) => setPriceMax(e.target.value)}
                className="w-16 bg-sage-50/50 border-none rounded-lg px-2 py-1 text-xs font-bold text-earth-900 focus:ring-2 focus:ring-sage-200 outline-none placeholder-sage-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 bg-white border border-sage-100 rounded-2xl pl-4 pr-2 py-2 shadow-sm min-w-[170px]">
            <ArrowUpDown size={14} className="text-sage-500 flex-shrink-0" />
            {isResolvingNearest ? (
              <div className="flex items-center gap-2 text-xs font-bold text-sage-600 px-1">
                <Loader2 size={14} className="animate-spin" />
                Detektovanje...
              </div>
            ) : (
              <Select
                options={SORT_OPTIONS}
                value={SORT_OPTIONS.find((o) => o.value === sortBy) || SORT_OPTIONS[0]}
                onChange={(opt) => handleSortChange(opt?.value || 'newest')}
                isSearchable={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                menuPlacement="auto"
                styles={sortSelectStyles}
                unstyled
                classNames={sortSelectClassNames}
                className="flex-1"
              />
            )}
          </div>

          <span className="text-xs font-bold text-sage-500 uppercase tracking-widest text-center whitespace-nowrap">
            {total} rezultata
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          {categoriesWithAll.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border shadow-sm ${
                activeCategory === cat
                  ? 'bg-sage-600 text-white border-sage-600 shadow-sage-600/20'
                  : 'bg-white text-sage-700 border-sage-100 hover:border-sage-300'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {error && !isLoading && (
        <div className="mb-8 px-5 py-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => (
              <ProductCardSkeleton key={`skeleton-${i}`} />
            ))
          : products.map((product) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="h-full"
              >
                <ProductCard product={product} onClick={() => openProduct(product)} />
              </motion.div>
            ))}
      </div>

      {!isLoading && hasMore && (
        <div ref={sentinelRef} className="flex items-center justify-center py-10">
          {isLoadingMore && (
            <div className="flex items-center gap-3 text-sage-600">
              <Loader2 className="animate-spin" size={20} />
              <span className="text-sm font-bold">Učitavanje još oglasa...</span>
            </div>
          )}
        </div>
      )}

      {!isLoading && !hasMore && products.length > 0 && (
        <p className="text-center text-xs text-sage-400 italic py-10">Prikazani su svi rezultati.</p>
      )}

      {!isLoading && products.length === 0 && !error && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-24 bg-sage-50 rounded-[3rem] border-2 border-dashed border-sage-200"
        >
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-sage-300 mx-auto mb-6 shadow-sm">
            <Search size={40} strokeWidth={1.5} />
          </div>
          <h3 className="text-2xl font-serif font-bold text-earth-900 mb-2">Nema rezultata</h3>
          <p className="text-sage-600 max-w-sm mx-auto mb-8">
            Nažalost, trenutno nema proizvoda koji odgovaraju vašim kriterijumima pretrage {globalSearchQuery ? `prema unosu "${globalSearchQuery}"` : ''}. Pokušajte sa drugim filterima.
          </p>
          <button
            onClick={() => {
              updateParams({ kategorija: '', opstina: '', cenaMin: '', cenaMax: '' });
              if (onResetSearch) onResetSearch();
            }}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-sage-600 text-white rounded-2xl font-bold hover:bg-sage-700 transition-all shadow-lg shadow-sage-600/20 active:scale-95 group"
          >
            <RotateCcw size={18} className="group-hover:-rotate-90 transition-transform duration-500" />
            Resetuj sve filtere
          </button>
        </motion.div>
      )}

      <ProductDetailsModal product={selectedProduct} onClose={() => closeProduct()} />

      <NearestLocationModal
        isOpen={isNearestModalOpen}
        initialCity={nearCity}
        onConfirm={(city) => {
          applyNearestFromCity(city);
          setIsNearestModalOpen(false);
        }}
        onClose={() => setIsNearestModalOpen(false)}
      />
    </section>
  );
}
